import React, { useRef, useEffect } from 'react';
import { toSafeFragment } from '../lib/html';

// Lightweight Word-like rich-text editor (no 3rd-party dependency). Emits HTML via onChange.
// The stored HTML is sanitized server-side before persistence and rendered with lib/html.

const TOOLS = [
  { cmd: 'bold', label: 'B', title: 'Bold', style: { fontWeight: 800 } },
  { cmd: 'italic', label: 'I', title: 'Italic', style: { fontStyle: 'italic' } },
  { cmd: 'underline', label: 'U', title: 'Underline', style: { textDecoration: 'underline' } },
  { cmd: 'formatBlock', arg: 'H2', label: 'H2', title: 'Heading' },
  { cmd: 'formatBlock', arg: 'H3', label: 'H3', title: 'Subheading' },
  { cmd: 'insertUnorderedList', label: '• List', title: 'Bulleted list' },
  { cmd: 'insertOrderedList', label: '1. List', title: 'Numbered list' },
  { cmd: 'formatBlock', arg: 'P', label: '¶', title: 'Paragraph' },
  { cmd: 'removeFormat', label: 'Clear', title: 'Clear formatting' },
];

const btnStyle = {
  minWidth: 30, height: 28, padding: '0 0.5rem', border: '1px solid #E2D9CD', background: '#fff',
  borderRadius: 7, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, color: '#57534E',
};

export function RichTextEditor({ value, onChange, placeholder = 'Start typing…', testId }) {
  const ref = useRef(null);

  // Sync external value in without clobbering the caret while the user is typing.
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== (value || '')) {
      el.replaceChildren(toSafeFragment(value));
    }
  }, [value]);

  const emit = () => onChange?.(ref.current?.innerHTML || '');

  const run = (tool) => (e) => {
    e.preventDefault();
    ref.current?.focus();
    if (tool.cmd === 'formatBlock') document.execCommand('formatBlock', false, tool.arg);
    else document.execCommand(tool.cmd, false, null);
    emit();
  };

  return (
    <div style={{ border: '1px solid #CBD5E1', borderRadius: 10, background: '#FAF7F2', overflow: 'hidden' }}>
      <style>{`.rte-content:empty:before{content:attr(data-placeholder);color:#A8A29E;}
.rte-content h2{font-size:1.15rem;font-weight:700;margin:.4rem 0;}
.rte-content h3{font-size:1rem;font-weight:700;margin:.35rem 0;}
.rte-content ul{list-style:disc;padding-left:1.25rem;margin:.35rem 0;}
.rte-content ol{list-style:decimal;padding-left:1.25rem;margin:.35rem 0;}
.rte-content p{margin:.3rem 0;}`}</style>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '0.4rem 0.5rem', borderBottom: '1px solid #ECE7DE', background: '#F1EAE1' }}>
        {TOOLS.map((t) => (
          <button
            key={t.label}
            type="button"
            data-testid={`rte-${t.cmd}${t.arg ? '-' + t.arg : ''}`}
            title={t.title}
            onMouseDown={run(t)}
            style={{ ...btnStyle, ...(t.style || {}) }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div
        ref={ref}
        className="rte-content"
        data-testid={testId}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder}
        style={{ minHeight: 150, maxHeight: 340, overflowY: 'auto', padding: '0.75rem 0.875rem', fontSize: '0.875rem', color: '#002A38', lineHeight: 1.6, outline: 'none' }}
      />
    </div>
  );
}

export default RichTextEditor;
